import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import SelectInput from "@builder/components/general/SelectInput";
import Toggle from "@builder/components/general/Toggle";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty, updateNodeQuery } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { QueryDataTables } from "@common/build/constants";
import { BuildNodeQuery, CustomerQueries, DynamicGridNode, QueryOrderByOptions } from "@common/build/types";
import { useEffect, useMemo, useState, useCallback } from "react";
import useDebouncedCallback from "@builder/hooks/useDebounceCallback";
import QueryRadio from "./QueryRadio";
import "./style.scss";

type CustomerSource = Exclude<BuildNodeQuery<"customers">["queries"]["customerSource"], undefined>;

type RadioOption = {
    label: string;
    value: CustomerQueries | "none";
    id: string; // Added for unique identification
};

const radioOptions: RadioOption[] = [
    { label: "All customers", value: "none", id: "none" },
    { label: "Created at current Outlet", value: { customerCreatedAtOutlet: "current" }, id: "created" },
    { label: "Ever active at current Outlet", value: { customerEverActiveInOutlet: "current" }, id: "active" },
    { label: "Any activity in the last 12 months", value: { customerLastActiveMonthsAgo: 12 }, id: "recent" }
];

const CustomerQuery = ({ parent }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode) as DynamicGridNode;
    const query = (node?.query || {}) as BuildNodeQuery<"customers">;
    const initialQueries = query.queries || {};

    // Order state
    const [orderBy, setOrderBy] = useState<QueryOrderByOptions["customers"] | undefined>(query.order?.orderBy || "firstName A-Z");
    const [ascending, setAscending] = useState<boolean>(query.order?.orderDirection === "ascending");

    // Customer source state
    const [customerSource, setCustomerSource] = useState<CustomerSource>(initialQueries.customerSource || "all");

    // Radio filter state
    const initialCurrentFilter = useMemo(() => {
        const currentFilter = radioOptions.find(option => {
            if (option.value === "none") return false;
            const key = Object.keys(option.value)[0] as keyof CustomerQueries;
            return initialQueries[key] === option.value[key];
        });
        return currentFilter?.id || "none";
    }, [initialQueries]);

    const [selectedRadioId, setSelectedRadioId] = useState<string>(initialCurrentFilter);

    const customerSourceOptions: Record<CustomerSource, string> = {
        all: "All customer sources",
        "in-store": "POS only",
        thirdParty: "3rd-party sources only"
    };

    const applyQuery = useCallback(() => {
        const newQuery: BuildNodeQuery<"customers"> = {
            dataType: QueryDataTables.CUSTOMERS,
            limit: 50,
            queries: {}
        };

        // Handle order
        if (orderBy) {
            const orderDirection = ascending ? "ascending" : "descending";
            newQuery.order = {
                orderBy,
                orderDirection
            };
        }

        // Handle customer source
        if (customerSource && customerSource !== "all") {
            newQuery.queries.customerSource = customerSource;
        }

        // Handle radio filters
        const selectedOption = radioOptions.find(option => option.id === selectedRadioId);
        if (selectedOption && selectedOption.value !== "none") {
            Object.assign(newQuery.queries, selectedOption.value);
        }

        dispatch(updateNodeQuery(newQuery));
    }, [dispatch, orderBy, ascending, customerSource, selectedRadioId]);

    const debouncedApplyQuery = useDebouncedCallback(applyQuery, 300);

    // Auto-apply query when any filter changes
    useEffect(() => {
        debouncedApplyQuery();
    }, [orderBy, ascending, customerSource, selectedRadioId]);

    return (
        <>
            <SidebarCollapse title="Query" parent={parent}>
                <Toggle
                    label="Group by first letter"
                    checked={!!node.groupBy}
                    callback={value => {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: ["groupBy"],
                                value: "firstName",
                                remove: !value
                            })
                        );
                    }}
                />
                {node.groupBy && (
                    <SelectInput
                        title="Group by"
                        className="full-width-select"
                        options={{
                            firstName: "First Name",
                            lastName: "Last Name",
                            createdAt: "Created At"
                        }}
                        value={node.groupBy}
                        callback={value => {
                            dispatch(
                                updateNodeProperty({
                                    propertyPath: ["groupBy"],
                                    value
                                })
                            );
                        }}
                    />
                )}
                <div className="dividing-line"></div>
                <SelectInput
                    className="full-width-select"
                    title="Sort by"
                    options={{
                        firstName: "First Name A-Z",
                        lastName: "Last Name A-Z"
                    }}
                    value={orderBy}
                    callback={(value: string) => setOrderBy(value as QueryOrderByOptions["customers"])}
                />
                <SelectInput
                    title="Order"
                    options={{
                        ascending: "Ascending",
                        descending: "Descending"
                    }}
                    value={ascending ? "ascending" : "descending"}
                    callback={(value: string) => setAscending(value === "ascending")}
                />

                <SelectInput
                    className="full-width-select"
                    title="Show customer sources"
                    options={customerSourceOptions}
                    value={customerSource}
                    callback={(value: string) => setCustomerSource(value as CustomerSource)}
                />
                <div className="dividing-line"></div>
                <QueryRadio
                    selected={selectedRadioId}
                    options={radioOptions}
                    isChecked={(option, selected) => option.id === selected}
                    getLabel={option => option.label}
                    callback={option => setSelectedRadioId(option.id)}
                />
            </SidebarCollapse>
        </>
    );
};

export default CustomerQuery;
